import React, { useState } from 'react';
import {
  Box, Typography, Button, RadioGroup, FormControlLabel, Radio, Stepper, Step, StepLabel, Paper, List, ListItem, ListItemText, Alert
} from '@mui/material';
import OverviewContent from './pr_eligibility/OverviewContent'; // Import the overview
import { questionnaire } from './pr_eligibility/QuestionnaireData'; // Import the questionnaire data
import PRInteractiveGuide from './PRInteractiveGuide'; // Import the new interactive guide

function IRCCInformation() {
  const [currentQuestionId, setCurrentQuestionId] = useState('start');
  const [answers, setAnswers] = useState({});
  const [showQuestionnaire, setShowQuestionnaire] = useState(false); // State to toggle questionnaire
  const [showResults, setShowResults] = useState(false);

  // Find the current question object from the imported data
  const currentQuestionData = questionnaire.find(q => q.id === currentQuestionId);

  const handleAnswer = (event) => {
    const { value } = event.target;
    const updatedAnswers = { ...answers, [currentQuestionId]: value };
    setAnswers(updatedAnswers);

    // Determine the next step based on the answer and questionnaire logic
    if (currentQuestionData?.next) {
      const nextId = currentQuestionData.next(value, updatedAnswers);
      const nextQuestion = questionnaire.find(q => q.id === nextId);
      if (nextQuestion?.outcome) {
        // If the next step is an outcome, set state to show results
        setCurrentQuestionId(nextId);
        setShowResults(true);
      } else if (nextQuestion) {
        // If it's another question, update the current question ID
        setCurrentQuestionId(nextId);
      } else {
        // Fallback if nextId doesn't match any question/outcome (shouldn't happen)
        setCurrentQuestionId('no_pathway_outcome');
        setShowResults(true);
      }
    } else {
      // If there's no 'next' function defined, assume it's the end and show results
      setShowResults(true);
    }
  };

  // Reset state to show the initial overview
  const handleRestart = () => {
    setAnswers({});
    setCurrentQuestionId('start');
    setShowResults(false);
    setShowQuestionnaire(false); // Go back to showing the overview
  };


  // Renders the current question UI
  const renderQuestion = () => {
    if (!currentQuestionData) return <Typography>Error: Question not found.</Typography>;
    const currentStepIndex = Object.keys(answers).length; // For Stepper progress

    return (
      <Box>
         {/* Basic Stepper for visual progress indication */}
         <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
           {[...Array(10)].map((_, index) => ( // Adjusted dummy steps
             <Step key={index}><StepLabel></StepLabel></Step>
           ))}
         </Stepper>
        <Typography variant="h6" gutterBottom sx={{ mb: { xs: 1, sm: 2 } }}>
          {currentQuestionData.question}
        </Typography>
        {/* Display informational notes if present for the question */}
        {currentQuestionData.info && (
          <Alert severity="info" sx={{ mb: { xs: 1, sm: 2 } }}>{currentQuestionData.info}</Alert>
        )}
        {/* Render radio button options */}
        <RadioGroup
          aria-labelledby={`question-${currentQuestionId}`}
          name={currentQuestionId}
          value={answers[currentQuestionId] || ''}
          onChange={handleAnswer}
        >
          {currentQuestionData.options.map((option) => (
            <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
          ))}
        </RadioGroup>
         {/* Button to go back to the overview */}
         <Button variant="outlined" onClick={handleRestart} sx={{ mt: { xs: 2, sm: 3 }, mr: 1 }}>
           Back to Overview
         </Button>
      </Box>
    );
  };

  // Renders the final results/outcome UI
  const renderResults = () => {
    const resultData = currentQuestionData; // The current node should be the outcome node
    if (!resultData || !resultData.outcome) return <Typography>Error: Results not found.</Typography>;

    // Generate specific PNP suggestions based on answers if applicable
    let pnpSuggestions = '';
    if (resultData.id === 'pnp_no_offer_outcome') {
        const provinceInterest = answers['pnp_no_offer_province_check'];
        const occupationInterest = answers['pnp_no_offer_occupation_check'];
        pnpSuggestions = ' Consider researching:';
        if (provinceInterest === 'Saskatchewan' || provinceInterest === 'Flexible / No specific connection') pnpSuggestions += ' Saskatchewan ISW (if occupation eligible & points met),';
        if (provinceInterest === 'Ontario' || provinceInterest === 'Flexible / No specific connection') pnpSuggestions += ' Ontario Human Capital/Graduate streams (if eligible),';
        if (provinceInterest === 'Manitoba') pnpSuggestions += ' Manitoba streams (if you have connections),';
        if (provinceInterest === 'Newfoundland and Labrador' || provinceInterest === 'Flexible / No specific connection') pnpSuggestions += ' NL Priority Skills (if occupation/education eligible),';
        // Check French answer if available (might not be if user skipped Quebec path)
        const frenchLevel = answers['quebec_french'];
        if (provinceInterest === 'New Brunswick' && frenchLevel && (frenchLevel.includes('Fluent') || frenchLevel.includes('Intermediate'))) pnpSuggestions += ' NB Strategic Initiative (Francophone stream),';
        if (occupationInterest === 'Yes, likely in a targeted field') pnpSuggestions += ' streams targeting your specific occupation in various provinces.';
        pnpSuggestions = pnpSuggestions.replace(/,\s*$/, '.'); // Clean up trailing comma
         if (pnpSuggestions === ' Consider researching.') pnpSuggestions = ''; // Remove if no specific suggestions added
    }
     if (resultData.id === 'pnp_job_offer_outcome') {
        const province = answers['job_offer_details'];
        const connections = answers['pnp_job_offer_province_check'];
        pnpSuggestions = ` Focus on ${province}'s employer-driven streams.`;
        if (connections && connections !== 'No specific connections other than the job offer') {
            pnpSuggestions += ` Your connections (${connections.replace('Yes, ','').toLowerCase()}) may further strengthen your application or open other provincial options.`;
        }
     }

    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: { xs: 1, sm: 2 } }}>Potential Eligibility Assessment</Typography>
        {/* Display identified pathways */}
        {resultData.pathways && resultData.pathways.length > 0 && (
          <>
            <Typography paragraph>Based on your answers and the 2025 eligibility document, you might be eligible for the following pathway(s):</Typography>
            <List dense>
              {resultData.pathways.map((pathway) => (
                <ListItem key={pathway}>
                  <ListItemText primary={pathway} />
                </ListItem>
              ))}
            </List>
          </>
        )}
        {/* Display the outcome message and any generated PNP suggestions */}
        <Typography paragraph sx={{ mt: { xs: 1, sm: 2 } }}>
            {resultData.message}
            {pnpSuggestions && <span style={{ fontWeight: 'bold' }}>{pnpSuggestions}</span>}
        </Typography>
        {/* Standard Disclaimer */}
        <Alert severity="warning" sx={{ mt: { xs: 2, sm: 3 } }}>
          Disclaimer: This is a preliminary assessment based *exclusively* on the trackcopr data (reflecting information as of early 2025). Immigration programs, criteria, processing times, and pilot statuses change frequently. This tool does not guarantee eligibility or provide immigration advice. Always consult the official Immigration, Refugees and Citizenship Canada (IRCC) website and relevant provincial government websites for the most accurate and up-to-date information before making any decisions.
        </Alert>
        {/* Button to restart or go back to overview */}
        <Button variant="contained" onClick={handleRestart} sx={{ mt: { xs: 2, sm: 3 } }}>
          Start Over / View Overview
        </Button>
      </Box>
    );
  };

  // Main component render logic: Show Overview or Questionnaire/Results
  return (
    <Paper elevation={3} sx={{ p: { xs: 1, sm: 2, md: 3 }, mt: { xs: 1, sm: 2 } }}>
      {/* Render the PR Flowchart at the top */}
      <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}> {/* Add some margin below the flowchart */}
        <Typography variant="h5" gutterBottom component="div" sx={{ textAlign: 'center', mb: 2 }}>
          General PR Pathways Overview
        </Typography>
        <PRInteractiveGuide />
      </Box>

      <hr style={{ margin: '2rem 0' }} /> {/* Add a separator */}

      <Typography variant="h4" gutterBottom component="div" sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center' }}>
        Canadian PR Information & Eligibility Tool
      </Typography>

      {!showQuestionnaire ? (
        // If not showing questionnaire, display the overview and start button
        <>
          <OverviewContent />
        </>
      ) : (
        // If showing questionnaire, display either the current question or the results
        !showResults ? renderQuestion() : renderResults()
      )}
    </Paper>
  );
}

export { IRCCInformation };